import React, { useState, useImperativeHandle, forwardRef } from 'react';
import { StyleSheet, Platform } from 'react-native';
import {
  CodeField,
  Cursor,
  useBlurOnFulfill,
  useClearByFocusCell,
  MaskSymbol,
  isLastFilledCell,
} from 'react-native-confirmation-code-field';

import { VFText } from '@vfgroup-oneplatform/foundation/Components';
import { TextPropTypes } from 'deprecated-react-native-prop-types';
import PropTypes from 'prop-types';

import LoginTestKeys from '../../../Utils/LoginTestKeys';

import Styles from './VFCodeInput.Styles';

function getCellStyle(isError, isFocused, symbol, colors, codeInputTextStyle) {
  const isIOSDevice = Platform.OS === 'ios';
  const isWeb = Platform.OS === 'web'

  const stylesArr = [Styles.codeInputText(colors), codeInputTextStyle];

  if (isError) {
    stylesArr.push({ borderColor: colors.textInput.error });
  }

  if (isFocused) {
    stylesArr.push({
      borderWidth: 2.1,
      borderColor: colors.textInput.tintColor,
      ...(isIOSDevice || isWeb ? Styles.codeInputIOSCursorAlignment : {})
    });
  }

  if (symbol) {
    stylesArr.push({
      ...(isIOSDevice || isWeb ? Styles.codeInputIOSSymbolAlignment : {})
    });
  }

  return StyleSheet.flatten(stylesArr);
}

function smsAccessibilityLabel(index) {
  return `${LoginTestKeys.loginSmsCode}${index + 1}`;
}

function onChangeText(text, setValue, codeLength, onFulfill) {
  setValue(text);

  if (text && text.length === codeLength) {
    onFulfill(text);
  }
}

const VFCodeInput = forwardRef(
  (
    {
      codeLength,
      codeInputTextStyle,
      onFulfill,
      onFocus,
      isError,
      colors,
      autoFocus,
      keyboardType,
    },
    forwardedRef,
  ) => {
    const [value, setValue] = useState('');
    const ref = useBlurOnFulfill({ value, cellCount: codeLength });
    const [props, getCellOnLayoutHandler] = useClearByFocusCell({
      value,
      setValue,
    });

    useImperativeHandle(forwardedRef, () => ({
      ...(ref.current || {}),
      clear: () => {
        ref.current?.clear();
        setValue('');
      },
    }));

    const renderCell = ({ index, symbol, isFocused }) => {
      let textChild = null;

      if (symbol) {
        textChild = (
          <MaskSymbol
            maskSymbol="•"
            isLastFilledCell={isLastFilledCell({ index, value })}
            delay={100} // originally was 2 which is barely visible.
          >
            {symbol}
          </MaskSymbol>
        );
      } else if (isFocused) {
        textChild = <Cursor />;
      }

      return (
        <VFText
          key={index}
          localize={false}
          style={getCellStyle(isError, isFocused, symbol, colors, codeInputTextStyle)}
          onLayout={getCellOnLayoutHandler(index)}
          testID={smsAccessibilityLabel(index)}
        >
          {textChild}
        </VFText>
      );
    };

    return (
      <CodeField
        ref={ref}
        {...props}
        onFocus={onFocus}
        value={value}
        onChangeText={text => onChangeText(text, setValue, codeLength, onFulfill)}
        cellCount={codeLength}
        rootStyle={Styles.codeFieldRoot}
        keyboardType={keyboardType ?? 'number-pad'}
        textContentType="oneTimeCode"
        renderCell={renderCell}
        autoFocus={autoFocus}
        contextMenuHidden
        selectionColor="transparent"
        testID={LoginTestKeys.loginSmsCodeValid}
        RootProps={{
          accessibilityLabel: LoginTestKeys.loginSmsCode,
        }}
      />
    );
  },
);

VFCodeInput.displayName = 'VFCodeInput';

VFCodeInput.defaultProps = {
  codeLength: 4,
  autoFocus: true,
};

VFCodeInput.propTypes = {
  codeLength: PropTypes.number.isRequired,
  codeInputTextStyle: TextPropTypes.style,
  onFulfill: PropTypes.func.isRequired,
  onFocus: PropTypes.func.isRequired,
  autoFocus: PropTypes.bool,
  isError: PropTypes.bool.isRequired,
  colors: PropTypes.object.isRequired,
  keyboardType: PropTypes.string,
};

export { getCellStyle, smsAccessibilityLabel, onChangeText };

export default VFCodeInput;
